import { useEffect, useState } from 'react';
import type {
  ApiClient, Kabeltrassenfelder, Kabeltrassenerhebung, Kabeltrassenprodukte,
} from '../api';
import {
  ContentSection, DataTable, EmptyState, ErrorState, KpiCard, KpiReihe,
  LoadingState, StatusBadge, type Spalte,
} from '../muster';

/**
 * Die vier Ansichten der Kabeltrassen, die nicht auf die Bestandsseite passen.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * HELLGRÜN UND HELLGELB
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Hellgrün: Der Wert steht im Revit-Modell und kommt von dort.
 * Hellgelb: Es gibt im Modell keinen Ort dafür — er lebt nur in der Plattform.
 *
 * Was später nach Revit zurückgeschrieben wird, wird nach vollständiger
 * Plattform entschieden. Bis dahin ist die Farbe eine Auskunft über die
 * HERKUNFT und kein Versprechen über den Rückweg.
 */

// Die frueheren Zeilenfarben HELLGRUEN/HELLGELB stehen hier nicht mehr: Die
// Herkunft ist seit Paket U6b eine eigene Spalte mit Zeichen UND Text
// (Abschnitt 18 - Status nie allein ueber Farbe).

const zahl = (n: number) => n.toLocaleString('de-DE');

// Der frühere Baum (Ebene → Abschnitt → Trassenart) stand hier. Er ist
// weggefallen: Der Bestandsbaum in ElektroTrassenbestand.tsx kann alles, was
// er konnte, und zusätzlich die Kategorie an der Spitze und den Raum als
// vierte Ebene. Zwei Bäume nebeneinander wären zwei Antworten auf dieselbe
// Frage — und irgendwann zwei verschiedene.

// ═══════════════════════════════════════════════════════════════════════════
// DIE MERKMALSERHEBUNG — WO DIE STROMART STEHEN KÖNNTE
// ═══════════════════════════════════════════════════════════════════════════
//
// Die wichtigste Ansicht dieses Bereichs, solange 37.932 Bauteile auf
// 'unbestimmt' stehen. Sie beantwortet die Frage, an der alles hängt: Wo hat
// der Elektroplaner die Stromart hingeschrieben?
//
// Ein geratenes Feld hätte die Seite vollständig aussehen lassen und jede
// Zuordnung falsch gemacht. Deshalb wird gezählt statt gewählt.

export function Merkmalserhebung(
  { api, projektId }: { api: ApiClient; projektId: string },
) {
  const [e, setE] = useState<Kabeltrassenerhebung | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    api.kabeltrassenerhebung(projektId)
      .then(setE)
      .catch((x) => setFehler(x instanceof Error ? x.message : String(x)));
  }, [api, projektId]);

  if (fehler) {
    return (
      <ErrorState
        meldung="Die Merkmalserhebung konnte nicht geladen werden."
        technisch={fehler}
      />
    );
  }

  if (!e) return <LoadingState text="Merkmale werden ausgezählt …" zeilen={8} spalten={5} />;

  const zeilen = e.zeilen ?? [];
  const gefuellt = zeilen.filter((z) => z.gefuellt > 0);
  const leer = zeilen.filter((z) => z.gefuellt === 0);

  // Ein Feld mit fast so vielen verschiedenen Werten wie gefüllten Zeilen
  // trägt eine Kennung je Bauteil und keine Einteilung — als Regelgrundlage
  // unbrauchbar.
  const istBrauchbar = (z: typeof zeilen[number]) =>
    z.verschiedene > 0 && z.verschiedene < 40 && z.gefuellt / z.verschiedene >= 2;

  const spalten: Spalte<typeof zeilen[number]>[] = [
    {
      schluessel: 'feld', titel: 'Feld', immerSichtbar: true, wert: (z) => z.feld,
      inhalt: (z) => <code>{z.feld}</code>,
    },
    {
      schluessel: 'gefuellt', titel: 'gefüllt', art: 'zahl', wert: (z) => z.gefuellt,
      inhalt: (z) => (
        <>
          {zahl(z.gefuellt)}
          <span className="schwach"> / {zahl(z.gesamt)}</span>
        </>
      ),
    },
    {
      schluessel: 'verschiedene', titel: 'verschiedene Werte', art: 'zahl',
      wert: (z) => z.verschiedene,
    },
    {
      schluessel: 'werte', titel: 'häufigste Werte', sortierbar: false,
      wert: (z) => z.werte, inhalt: (z) => <>{werteliste(z.werte)}</>,
    },
    {
      schluessel: 'brauchbar', titel: 'brauchbar?', art: 'status', breite: '260px',
      wert: (z) => (istBrauchbar(z)
        ? 'als Regelgrundlage geeignet'
        : 'eine Kennung je Bauteil — keine Einteilung'),
      inhalt: (z) => (istBrauchbar(z)
        ? <StatusBadge zustand="gut" text="als Regelgrundlage geeignet" klein />
        : (
          <StatusBadge
            zustand="warnung" klein
            text="eine Kennung je Bauteil — keine Einteilung"
          />
        )),
    },
  ];

  return (
    <>
      <ContentSection
        titel="Wo könnte die Stromart stehen?"
        beschreibung={'Wo der Elektroplaner die Stromart hinschreibt, ist bei jedem '
          + 'Planer anders: im Arbeitssatz, im Kommentar, in einer Kennung, in einem '
          + 'eigenen Parameter. Die Aufnahme liest deshalb alle Kandidaten und zählt '
          + 'aus. Erst danach wird die Regel gesetzt.'}
      >
        {(e.hinweise ?? []).map((h, i) => (
          <div key={i} className="hinweis" style={{ display: 'block' }}>{h}</div>
        ))}
      </ContentSection>

      <ContentSection
        titel={`Gefüllt — ${gefuellt.length} von ${zeilen.length} Kandidaten`}
        nackt
      >
        <DataTable
          name="Merkmalskandidaten"
          spalten={spalten}
          zeilen={gefuellt}
          schluessel={(z) => z.feld}
          ausfuhr
          ausfuhrDateiname="merkmalserhebung"
          leer={(
            <EmptyState
              titel="Kein Kandidat trägt einen Wert."
              erklaerung={'Nachgesehen und nichts gefunden. Solange kein Feld gefüllt '
                + 'ist, bleibt jede Trasse „ohne Stromart“ — das ist gewollt.'}
            />
          )}
        />
      </ContentSection>

      {leer.length > 0 && (
        <ContentSection
          titel={`Leer — ${leer.length} Kandidat(en)`}
          beschreibung={'Nachgesehen und nichts gefunden. Das ist eine Auskunft und '
            + 'keine Lücke: Beim nächsten Mal muss niemand dieselben Felder noch '
            + 'einmal prüfen.'}
        >
          <p style={{ fontSize: 12 }}>
            {leer.map((z) => z.feld).join(' · ')}
          </p>
        </ContentSection>
      )}
    </>
  );
}

/** Die häufigsten Werte aus dem JSON — höchstens sechs. */
function werteliste(roh: string | null) {
  if (!roh) return <span className="schwach">—</span>;

  try {
    const w = JSON.parse(roh) as Record<string, number>;

    const oben = Object.entries(w)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6);

    if (oben.length === 0) return <span className="schwach">—</span>;

    return oben.map(([k, n]) => `${k} × ${zahl(n)}`).join(' · ');
  } catch {
    return <span className="schwach">nicht lesbar</span>;
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// DER FELDKATALOG — HELLGRÜN UND HELLGELB
// ═══════════════════════════════════════════════════════════════════════════

export function Trassenfelder(
  { api, projektId }: { api: ApiClient; projektId: string },
) {
  const [f, setF] = useState<Kabeltrassenfelder | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    api.kabeltrassenfelder(projektId)
      .then(setF)
      .catch((x) => setFehler(x instanceof Error ? x.message : String(x)));
  }, [api, projektId]);

  if (fehler) {
    return (
      <ErrorState meldung="Der Feldkatalog konnte nicht geladen werden." technisch={fehler} />
    );
  }

  if (!f) return <LoadingState text="Feldkatalog wird geladen …" zeilen={8} spalten={5} />;

  const felder = f.felder ?? [];
  const gruppen = [...new Set(felder.map((x) => x.gruppe ?? 'Übrige'))];

  const spalten: Spalte<typeof felder[number]>[] = [
    {
      schluessel: 'feld', titel: 'Feld', immerSichtbar: true,
      wert: (x) => `${x.bezeichnung} ${x.feldschluessel}`,
      inhalt: (x) => (
        <>
          <strong>{x.bezeichnung}</strong>
          <br />
          <code className="schwach" style={{ fontSize: 11 }}>{x.feldschluessel}</code>
        </>
      ),
    },
    {
      schluessel: 'einheit', titel: 'Einheit', wert: (x) => x.einheit,
      inhalt: (x) => <>{x.einheit ?? <span className="schwach">—</span>}</>,
    },
    {
      schluessel: 'gilt', titel: 'gilt für', wert: (x) => x.gruppen ?? 'alle',
      inhalt: (x) => <span className="schwach">{x.gruppen ?? 'alle'}</span>,
    },
    {
      schluessel: 'herkunft', titel: 'Herkunft', art: 'status', breite: '230px',
      wert: (x) => (x.ausModellGelesen ? 'Modell' : 'Plattform'),
      inhalt: (x) => (x.ausModellGelesen
        ? (
          <>
            <StatusBadge zustand="gut" text="aus dem Modell" klein />
            <br />
            <code className="schwach" style={{ fontSize: 11 }}>{x.revitParameter}</code>
          </>
        )
        : <StatusBadge zustand="info" text="nur in der Plattform" klein />),
    },
    {
      schluessel: 'hinweis', titel: 'Anmerkung', wert: (x) => x.hinweis,
      inhalt: (x) => <span className="schwach">{x.hinweis ?? ''}</span>,
    },
  ];

  return (
    <>
      <ContentSection
        titel="Die Felder und ihre Herkunft"
        beschreibung={'Wer einen Plattformwert im Modell sucht, sucht vergeblich; wer '
          + 'einen Modellwert hier ändert, ändert ihn bis zum nächsten Lesen. Was '
          + 'später nach Revit zurückgeschrieben wird, wird nach vollständiger '
          + 'Plattform entschieden — bis dahin ist die Herkunft eine Auskunft und '
          + 'kein Versprechen über den Rückweg.'}
      >
        <KpiReihe>
          <KpiCard titel="aus dem Modell" wert={f.ausModellGelesen} />
          <KpiCard titel="in der Plattform gepflegt" wert={f.ausPlattform} />
          <KpiCard titel="gepflegte Werte" wert={(f.werte ?? []).length} />
        </KpiReihe>
      </ContentSection>

      {gruppen.map((g) => (
        <ContentSection key={g} titel={g} nackt>
          <DataTable
            name={g}
            spalten={spalten}
            zeilen={felder.filter((x) => (x.gruppe ?? 'Übrige') === g)}
            schluessel={(x) => x.feldschluessel}
            // Die Herkunft steht als Marke in einer eigenen Spalte und nicht
            // mehr nur als Zeilenfarbe: Farbe allein trägt keine Aussage
            // (Abschnitt 18).
            ausfuhr
            ausfuhrDateiname={`trassenfelder-${g}`}
            dicht
            leer={(
              <EmptyState
                titel={`In der Gruppe „${g}“ steht kein Feld.`}
                erklaerung="Der Feldkatalog entsteht aus der Elektroaufnahme."
              />
            )}
          />
        </ContentSection>
      ))}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// DIE PRODUKTDATEN DER TRASSENSYSTEME UND LEERROHRE
// ═══════════════════════════════════════════════════════════════════════════

export function Trassenprodukte(
  { api, projektId }: { api: ApiClient; projektId: string },
) {
  const [p, setP] = useState<Kabeltrassenprodukte | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    api.kabeltrassenprodukte(projektId)
      .then(setP)
      .catch((x) => setFehler(x instanceof Error ? x.message : String(x)));
  }, [api, projektId]);

  if (fehler) {
    return (
      <ErrorState meldung="Die Produktdaten konnten nicht geladen werden." technisch={fehler} />
    );
  }

  if (!p) return <LoadingState text="Produktdaten werden geladen …" zeilen={8} spalten={5} />;

  const fabrikate = p.fabrikate ?? [];
  const arten = [...new Set(fabrikate.map((x) => x.geraeteart))].sort();

  const bezeichnung = (schluessel: string) =>
    (p.felder ?? []).find((f) => f.feldschluessel === schluessel)?.bezeichnung
    ?? schluessel;

  const spalten: Spalte<typeof fabrikate[number]>[] = [
    {
      schluessel: 'merkmal', titel: 'Merkmal', immerSichtbar: true,
      wert: (x) => bezeichnung(x.feldschluessel),
    },
    {
      schluessel: 'wert', titel: 'Wert', wert: (x) => x.wert ?? x.zahlwert,
      inhalt: (x) => (x.herkunft === 'offen'
        ? <StatusBadge zustand="warnung" text={String(x.wert ?? x.zahlwert ?? 'offen')} klein />
        : <>{x.wert ?? x.zahlwert ?? '—'}</>),
    },
    {
      schluessel: 'typmuster', titel: 'Typmuster', wert: (x) => x.typmuster,
      inhalt: (x) => <span className="schwach">{x.typmuster ?? '—'}</span>,
    },
    {
      schluessel: 'herkunft', titel: 'Herkunft', art: 'status', breite: '200px',
      wert: (x) => `${x.herkunft}${x.geprueft ? ' · geprüft' : ' · ungeprüft'}`,
      inhalt: (x) => (x.geprueft
        ? <StatusBadge zustand="gut" text={`${x.herkunft} · geprüft`} klein />
        : <StatusBadge zustand="warnung" text={`${x.herkunft} · ungeprüft`} klein />),
    },
    {
      schluessel: 'bemerkung', titel: 'Anmerkung', wert: (x) => x.bemerkung,
      inhalt: (x) => <span className="schwach">{x.bemerkung ?? ''}</span>,
    },
  ];

  return (
    <>
      <ContentSection titel="Produktdaten">
        {(p.hinweise ?? []).map((h, i) => (
          <div key={i} className="hinweis" style={{ display: 'block' }}>{h}</div>
        ))}

        {arten.length === 0 && (
          <EmptyState
            titel="Für die Trassensysteme ist noch kein Produkt hinterlegt."
            erklaerung={'Fabrikat, Typ und Zulassung leben in der Plattform — im '
              + 'Modell gibt es dafür keinen Ort.'}
          />
        )}
      </ContentSection>

      {arten.map((art) => (
        <ContentSection key={art} titel={art} nackt>
          <DataTable
            name={art}
            spalten={spalten}
            zeilen={fabrikate.filter((x) => x.geraeteart === art)}
            schluessel={(x) => String(x.id)}
            ausfuhr
            ausfuhrDateiname={`trassenprodukte-${art}`}
            dicht
            leer={(
              <EmptyState
                titel={`Für „${art}“ ist kein Merkmal gepflegt.`}
                erklaerung="Produktdaten werden in der Plattform gepflegt."
              />
            )}
          />
        </ContentSection>
      ))}
    </>
  );
}
